import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Grid,
  TextField,
  MenuItem,
  Pagination,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { getBookings } from "../api/bookings";
import {
  getUserProfile,
  logoutUser,
  updateUserProfile,
  changePassword,
} from "../api/users";
import { userHasGroup, isPlatformAdminUser, isTenantAdminUser } from "../utils/auth";
import BookingList from "../components/BookingList";
import SkeletonTripList from "../components/SkeletonTripList";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const ROWS_PER_PAGE = 5;
const TAB_BOOKINGS = "bookings";
const TAB_SETTINGS = "settings";

const FILTER_ALL = "all";
const FILTER_UPCOMING = "upcoming";
const FILTER_PAST = "past";
const FILTER_UNPAID = "unpaid";
const FILTER_CANCELLED = "cancelled";

function bookingListParams(filterId) {
  const today = dayjs().format("YYYY-MM-DD");
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  switch (filterId) {
    case FILTER_UPCOMING:
      return {
        date_from: today,
        status: "paid,not_paid,pending,processing",
      };
    case FILTER_PAST:
      return { date_to: yesterday };
    case FILTER_UNPAID:
      return { status: "pending,not_paid,processing" };
    case FILTER_CANCELLED:
      return { status: "cancelled,refunded,refunding,failed,no_show" };
    default:
      return {};
  }
}

const DEFAULT_FORM_DATA = {
  first_name: "",
  last_name: "",
  phone_number: null,
  gender: "",
  birth_date: null,
  saved: false,
  old_password: "",
  new_password: "",
  confirm_password: "",
};

export default function Profile() {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [bookings, setBookings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState(null);

  const activeTabRaw = searchParams.get("tab") || TAB_BOOKINGS;
  const activeTab =
    activeTabRaw === "privacy" ? TAB_SETTINGS : activeTabRaw;
  const bookingFilter = searchParams.get("filter") || FILTER_ALL;

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch user profile
  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      setUserLoading(true);
      setUserError(null);
      try {
        const userData = await getUserProfile();
        if (!cancelled) {
          setUser(userData.user);
          setFormData((prev) => ({
            ...prev,
            first_name: userData.user.first_name || "",
            last_name: userData.user.last_name || "",
            phone_number: userData.user.phone_number || "",
            gender: userData.user.gender || "",
            birth_date: userData.user.birth_date
              ? dayjs(userData.user.birth_date)
              : null,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setUserError(t("profile.loadError"));
        }
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Sync page from URL
  useEffect(() => {
    if (activeTab !== TAB_BOOKINGS) return;
    const qp = Number(searchParams.get("page") || "1");
    setPage(Number.isFinite(qp) && qp > 0 ? qp : 1);
  }, [activeTab, searchParams]);

  // Fetch bookings
  useEffect(() => {
    if (activeTab !== TAB_BOOKINGS) return;
    let cancelled = false;
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const response = await getBookings({
          page,
          page_size: ROWS_PER_PAGE,
          ui: 1,
          ...bookingListParams(bookingFilter),
        });
        if (!cancelled) {
          setBookings(response.results);
          setTotalCount(response.count);
        }
      } catch (err) {
        if (!cancelled) {
          enqueueSnackbar(t("profile.bookingsLoadError"), {
            variant: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBookings();
    return () => {
      cancelled = true;
    };
  }, [page, activeTab, bookingFilter, enqueueSnackbar, t]);

  const setBookingsQuery = useCallback(
    ({ filter = bookingFilter, page: nextPage = 1 } = {}) => {
      const params = { tab: TAB_BOOKINGS, page: String(nextPage) };
      if (filter && filter !== FILTER_ALL) params.filter = filter;
      setSearchParams(params);
    },
    [bookingFilter, setSearchParams]
  );

  const handleChangePage = useCallback(
    (_, newPage) => {
      setPage(newPage);
      setBookingsQuery({ page: newPage });
    },
    [setBookingsQuery]
  );

  const handleTabChange = useCallback(
    (tab) => {
      if (tab === TAB_BOOKINGS) {
        setBookingsQuery({ filter: FILTER_ALL, page: 1 });
      } else {
        setSearchParams({ tab });
      }
    },
    [setBookingsQuery, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filterId) => {
      setPage(1);
      setBookingsQuery({ filter: filterId, page: 1 });
    },
    [setBookingsQuery]
  );

  const handleInputChange = useCallback(
    (e) =>
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      })),
    []
  );

  const handleDateChange = useCallback((newDate) => {
    setFormData((prev) => ({ ...prev, birth_date: newDate }));
  }, []);

  const handlePhoneChange = useCallback((newVal) => {
    setFormData((prev) => ({ ...prev, phone_number: newVal }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!matchIsValidTel(formData.phone_number)) {
      newErrors.phone_number = t("checkout.invalidPhone");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.phone_number, t]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    setSaving(true);
    setErrors({});
    try {
      await updateUserProfile(formData);
      enqueueSnackbar(t("profile.profileUpdated"), { variant: "success" });
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        t("profile.updateError");
      setErrors({ general: msg });
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }, [formData, validateForm, enqueueSnackbar, t]);

  const handleChangePassword = useCallback(async () => {
    setErrors((prev) => ({ ...prev, old_password: "", new_password: "", confirm_password: "" }));
    const localErrors = {};
    if (!formData.old_password)
      localErrors.old_password = t("profile.enterCurrentPassword");
    if (!formData.new_password)
      localErrors.new_password = t("profile.enterNewPassword");
    if (formData.new_password !== formData.confirm_password) {
      localErrors.confirm_password = t("profile.passwordsMismatch");
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...localErrors }));
      return;
    }
    try {
      const res = await changePassword({
        old_password: formData.old_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });
      enqueueSnackbar(res.message || t("profile.passwordChanged"), { variant: "success" });
      setFormData((prev) => ({
        ...prev,
        old_password: "",
        new_password: "",
        confirm_password: "",
      }));
    } catch (err) {
      const backendError =
        err.response?.data?.error || t("profile.passwordChangeError");
      setErrors((prev) => ({ ...prev, old_password: backendError }));
      enqueueSnackbar(backendError, { variant: "error" });
    }
  }, [formData, enqueueSnackbar, t]);

  const totalPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));
  const showPagination = totalCount > ROWS_PER_PAGE;

  const navItems = [
    { id: TAB_BOOKINGS, label: t("profile.myBookings"), icon: <ReceiptIcon /> },
    { id: TAB_SETTINGS, label: t("profile.settings"), icon: <SettingsIcon /> },
  ];

  const bookingFilterItems = [
    { id: FILTER_ALL, label: t("profile.filterAll") },
    { id: FILTER_UPCOMING, label: t("profile.filterUpcoming") },
    { id: FILTER_PAST, label: t("profile.filterPast") },
    { id: FILTER_UNPAID, label: t("profile.filterUnpaid") },
    { id: FILTER_CANCELLED, label: t("profile.filterCancelled") },
  ];

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 2, pb: 6, px: { xs: 2, sm: 3 } }}
      aria-label={t("profile.ariaLabel")}
    >
      <Typography
        component="h1"
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          color: "text.primary",
          display: { xs: "block", md: "none" },
        }}
      >
        {t("profile.myProfile")}
      </Typography>

      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {userLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Skeleton
                  variant="circular"
                  width={96}
                  height={96}
                  sx={{ mx: "auto", mb: 2 }}
                />
                <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} />
                <Skeleton variant="text" width="60%" sx={{ mx: "auto" }} />
                <Box sx={{ mt: 3 }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={44}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            ) : userError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {userError}
              </Alert>
            ) : (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 96,
                      height: 96,
                      bgcolor: "primary.main",
                      fontSize: "2rem",
                      fontWeight: 600,
                    }}
                    aria-hidden
                  >
                    {user?.first_name && user?.last_name
                      ? `${user.first_name[0]}${user.last_name[0]}`
                      : user?.email?.charAt(0)?.toUpperCase() || "?"}
                  </Avatar>
                  <Typography
                    variant="h6"
                    component="p"
                    sx={{ mt: 2, fontWeight: 600, textAlign: "center" }}
                  >
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email}
                  </Typography>
                  {user?.email && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {user.email}
                    </Typography>
                  )}
                </Box>

                <List disablePadding>
                  {navItems.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={activeTab === item.id}
                      onClick={() => handleTabChange(item.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": { bgcolor: "primary.dark" },
                          "& .MuiListItemIcon-root": {
                            color: "primary.contrastText",
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  ))}

                  {userHasGroup("Company Admin") && (
                    <>
                      <ListItemButton
                        onClick={() => navigate("/administrator/dashboard")}
                        sx={{ borderRadius: 2, mb: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <AdminPanelSettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary={t("header.adminPanel")} />
                      </ListItemButton>
                      {isPlatformAdminUser() || isTenantAdminUser() ? (
                        <ListItemButton
                          onClick={() => navigate("/administrator/reconciliation")}
                          sx={{ borderRadius: 2, mb: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <AttachMoneyIcon />
                          </ListItemIcon>
                          <ListItemText primary={t("header.agentReconciliation")} />
                        </ListItemButton>
                      ) : null}
                    </>
                  )}
                  {userHasGroup("Company Driver") && (
                    <ListItemButton
                      onClick={() => navigate("/administrator/trips/passengers")}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <DirectionsBusIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("header.driverCabinet")} />
                    </ListItemButton>
                  )}

                  <ListItemButton
                    onClick={logoutUser}
                    sx={{
                      borderRadius: 2,
                      mt: 1,
                      color: "error.main",
                      "&:hover": { bgcolor: "error.light", color: "error.dark" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText primary={t("header.logout")} />
                  </ListItemButton>
                </List>
              </>
            )}
          </Paper>
        </Grid>

        {/* Content */}
        <Grid item xs={12} md={9}>
          {activeTab === TAB_BOOKINGS && (
            <>
              <Typography
                component="h2"
                variant="h5"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                {t("profile.myBookings")}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: 2 }}
              >
                {bookingFilterItems.map((item) => {
                  const selected = bookingFilter === item.id;
                  return (
                    <Chip
                      key={item.id}
                      label={item.label}
                      clickable
                      onClick={() => handleFilterChange(item.id)}
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                    />
                  );
                })}
              </Stack>

              {loading ? (
                <SkeletonTripList count={ROWS_PER_PAGE} />
              ) : bookings.length > 0 ? (
                <>
                  <BookingList bookings={bookings} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 3,
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("profile.shownCount", {
                        from: (page - 1) * ROWS_PER_PAGE + 1,
                        to: Math.min(page * ROWS_PER_PAGE, totalCount),
                        total: totalCount,
                      })}
                    </Typography>
                    {showPagination && (
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                        showFirstButton
                        showLastButton
                        disabled={loading}
                        size={isMobile ? "small" : "medium"}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 5,
                    borderRadius: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {bookingFilter === FILTER_ALL
                      ? t("profile.noBookingsYet")
                      : t("profile.noBookingsForFilter")}
                  </Typography>
                  {bookingFilter === FILTER_ALL ? (
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => navigate("/")}
                      size="large"
                    >
                      {t("search.findTicket")}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => handleFilterChange(FILTER_ALL)}
                    >
                      {t("profile.filterAll")}
                    </Button>
                  )}
                </Paper>
              )}
            </>
          )}

          {activeTab === TAB_SETTINGS && user && (
            <>
              <Typography
                component="h2"
                variant="h5"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                {t("profile.profileSettings")}
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                }}
              >
                {errors.general && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.general}
                  </Alert>
                )}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label={t("profile.firstName")}
                      name="first_name"
                      fullWidth
                      value={formData.first_name}
                      onChange={handleInputChange}
                      error={!!errors.first_name}
                      helperText={errors.first_name}
                      autoComplete="given-name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label={t("profile.lastName")}
                      name="last_name"
                      fullWidth
                      value={formData.last_name}
                      onChange={handleInputChange}
                      error={!!errors.last_name}
                      helperText={errors.last_name}
                      autoComplete="family-name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label={t("profile.gender")}
                      name="gender"
                      fullWidth
                      value={formData.gender}
                      onChange={handleInputChange}
                      error={!!errors.gender}
                      helperText={errors.gender}
                    >
                      <MenuItem value="">{t("profile.genderNotSpecified")}</MenuItem>
                      <MenuItem value="male">{t("profile.male")}</MenuItem>
                      <MenuItem value="female">{t("profile.female")}</MenuItem>
                      <MenuItem value="other">{t("profile.other")}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MuiTelInput
                      label={t("profile.phone")}
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      defaultCountry="UA"
                      fullWidth
                      error={!!errors.phone_number}
                      helperText={errors.phone_number}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="E-mail"
                      fullWidth
                      disabled
                      value={user?.email || ""}
                      helperText={t("profile.emailReadOnly")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label={t("profile.birthDate")}
                      value={formData.birth_date}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.birth_date,
                          helperText: errors.birth_date,
                        },
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? t("profile.saving") : t("profile.saveChanges")}
                  </Button>
                </Box>
              </Paper>

              <Typography
                component="h2"
                variant="h5"
                sx={{ fontWeight: 700, mt: 4, mb: 2 }}
              >
                {t("profile.changePasswordTitle")}
              </Typography>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label={t("profile.currentPassword")}
                      type="password"
                      fullWidth
                      value={formData.old_password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          old_password: e.target.value,
                        }))
                      }
                      error={!!errors.old_password}
                      helperText={errors.old_password}
                      autoComplete="current-password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("profile.newPassword")}
                      type="password"
                      fullWidth
                      value={formData.new_password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          new_password: e.target.value,
                        }))
                      }
                      error={!!errors.new_password}
                      helperText={errors.new_password}
                      autoComplete="new-password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("profile.confirmPassword")}
                      type="password"
                      fullWidth
                      value={formData.confirm_password}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          confirm_password: e.target.value,
                        }))
                      }
                      error={!!errors.confirm_password}
                      helperText={errors.confirm_password}
                      autoComplete="new-password"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    onClick={handleChangePassword}
                    color="primary"
                  >
                    {t("profile.changePasswordBtn")}
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
